import React, { useEffect, useRef, useState, memo, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAddress, IPlacesSuggestResponse } from 'Modules/SearchAddress/types';
import { IPlace } from 'Modules/Places/types';
import SearchAddressActions, {
  clearCurrentAddressAction,
  setCurrentAddressAction,
} from 'Modules/SearchAddress/actions';
import { MIN_LENGTH_SEARCH_QUERY, EMPTY_ARRAY, EMPTY_STRING, FAKE_SA_REQUEST, FAKE_CITY } from 'constants/constants';
import amplitude from 'utils/amplitude';
import { EnumEvents, EnumGtagEvents } from 'constants/enums';
import { useDebounce } from 'utils/hooks';
import { getCurrentAddress, getCurrentCityId } from 'Modules/SearchAddress/selectors';
import { useRequest } from 'swr.config';
import ShopActions from 'Modules/Shops/actions';
import { getCurrentShopId } from 'Modules/Shops/selectors';
import analyticsManager from 'utils/tagManager/manager';

export const ERROR_EMPTY = 'Введите свой адрес';
export const ERROR_NOT_FULL = 'Введите полный адрес';
export const ERROR_NOT_FOUND = 'Адрес не найден';

export interface IRenderProps {
  handleSubmit: (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => void;
  handleChangeAddress: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSelectAddress: (address: IAddress) => void;
  handleUpdateCurrentAddress: (address: IAddress) => void;
  handleResetAddress: () => void;
  handleResetErrors: () => void;
  addressesList: IAddress[];
  errorMsg: string;
  enteredAddress: string;
  isLoading: boolean;
}

export interface ISearchAddress {
  onSelectAddress?: (newAddress: IPlace, enteredAddress: string) => void;
  children: (args: IRenderProps) => JSX.Element;
}

const SearchAddress: React.FC<ISearchAddress> = ({ onSelectAddress, children }) => {
  const cityId = useSelector(getCurrentCityId);
  const currentAddress = useSelector(getCurrentAddress);
  const currentShopId = useSelector(getCurrentShopId);
  const [enteredAddress, setEnteredAddress] = useState<string>(EMPTY_STRING);
  const [addressesList, setAddressesList] = useState<IAddress[]>(EMPTY_ARRAY);
  const [addressWithKladr, setAddressWithKladr] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>(EMPTY_STRING);
  const dispatch = useDispatch();
  const debouncedQuery = useDebounce(enteredAddress, 500);
  const searchFromSuggestRef = useRef<boolean>(false);
  const prevCityId = useRef<number>(cityId);
  const hasCoordinate = useRef<boolean>(false);
  const isEmptyAddressWithKladr = !addressWithKladr?.object_kladr || !addressWithKladr?.house;
  const isDisabledSubmit =
    (!hasCoordinate.current && isEmptyAddressWithKladr) || enteredAddress.length < MIN_LENGTH_SEARCH_QUERY;
  const isEnabledSubmit = !isDisabledSubmit;
  const isAvailableLength = debouncedQuery.length >= MIN_LENGTH_SEARCH_QUERY;
  const shouldFetchSuggest =
    isAvailableLength && enteredAddress === debouncedQuery && !hasCoordinate.current && isEmptyAddressWithKladr;

  const { data } = useRequest<IPlacesSuggestResponse>(
    shouldFetchSuggest ? ['/places/address', { city_id: cityId, query: debouncedQuery }] : null
  );

  useEffect(() => {
    if (prevCityId.current !== cityId) {
      prevCityId.current = cityId;
      setEnteredAddress(EMPTY_STRING);
    }
  }, [cityId]);

  useEffect(() => {
    if (data?.list) {
      setAddressesList(data.list);
    }
  }, [data]);

  useEffect(() => {
    if (currentAddress.name) {
      setEnteredAddress(currentAddress.name);
      setAddressWithKladr(EMPTY_STRING);
      setErrorMsg(EMPTY_STRING);
    }
  }, [currentAddress.name]);

  useEffect(() => {
    // костыль для компонентов СА лендинге, для обновления кладра из сторонего компонента
    // нужно убрать как перейдем на новый геокодинг без кладра
    if (currentAddress.object_kladr) {
      setAddressWithKladr(currentAddress);
    }
  }, [currentAddress, currentAddress.object_kladr]);

  useEffect(() => {
    hasCoordinate.current = Boolean(currentAddress.coordinate);
    setAddressesList([]);
  }, [currentAddress.coordinate]);

  const handleChangeAddress = (e: ChangeEvent<HTMLInputElement>) => {
    hasCoordinate.current = false;
    setEnteredAddress(e.target.value);
    searchFromSuggestRef.current = false;
    setErrorMsg(EMPTY_STRING);
    setAddressWithKladr(EMPTY_STRING);
    currentAddress.name && dispatch(clearCurrentAddressAction());
    currentShopId && dispatch(ShopActions.clearCurrentShop());
  };

  const handleResetAddress = () => {
    hasCoordinate.current = false;
    setEnteredAddress(EMPTY_STRING);
    searchFromSuggestRef.current = false;
    setErrorMsg(EMPTY_STRING);
    setAddressWithKladr(EMPTY_STRING);
    setAddressesList(EMPTY_ARRAY);
    currentAddress.name && dispatch(clearCurrentAddressAction());
    currentShopId && dispatch(ShopActions.clearCurrentShop());
  };

  const handleSelectAddress = (address: IAddress) => {
    setEnteredAddress(address.name);
    searchFromSuggestRef.current = true;
    setAddressesList(EMPTY_ARRAY);
    setErrorMsg(EMPTY_STRING);
    setAddressWithKladr(address);
    currentShopId && dispatch(ShopActions.clearCurrentShop());
  };

  const handleUpdateCurrentAddress = (address: IAddress) => {
    dispatch(setCurrentAddressAction(address));
    searchFromSuggestRef.current = true;
    setAddressesList(EMPTY_ARRAY);
    setErrorMsg(EMPTY_STRING);
    currentShopId && dispatch(ShopActions.clearCurrentShop());
  };

  const handleResetErrors = () => {
    setErrorMsg(EMPTY_STRING);
  };

  const updateCurrentAddress = () => {
    (async () => {
      try {
        setIsLoading(true);

        const address = !hasCoordinate.current
          ? await dispatch(
              SearchAddressActions.updateCurrentAddressFromKladrQuery(
                cityId,
                addressWithKladr.object_kladr,
                addressWithKladr.house,
                enteredAddress
              )
            )
          : currentAddress;
        analyticsManager.gtag(EnumGtagEvents.SELECT_ADDRESS, { from: 'select_address' });
        onSelectAddress && onSelectAddress((address as unknown) as IPlace, enteredAddress);
      } catch (e) {
        setErrorMsg(ERROR_NOT_FOUND);
        if (searchFromSuggestRef.current) {
          amplitude.logEvent(EnumEvents.SA_FAIL_SUGGEST_GEOCODING, {
            'city id': cityId,
            query: enteredAddress,
          });
        }
      } finally {
        setIsLoading(false);
      }
    })();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isEnabledSubmit) {
      updateCurrentAddress();
    } else {
      if (enteredAddress.length) {
        setErrorMsg(ERROR_NOT_FULL);
      } else {
        setErrorMsg(ERROR_EMPTY);
      }
    }
  };

  useEffect(() => {
    if (debouncedQuery.length >= MIN_LENGTH_SEARCH_QUERY) {
      if (debouncedQuery.toLowerCase().includes('игорь')) {
        dispatch(SearchAddressActions.setIgor());
      }
      if (debouncedQuery.toLowerCase().includes(FAKE_SA_REQUEST)) {
        dispatch(SearchAddressActions.clearCurrentAddress());
        dispatch(SearchAddressActions.setCurrentCity(FAKE_CITY));
      }
    }
  }, [cityId, debouncedQuery, dispatch]);

  return children({
    handleSubmit,
    handleChangeAddress,
    handleSelectAddress,
    handleResetAddress,
    handleResetErrors,
    handleUpdateCurrentAddress,
    errorMsg,
    enteredAddress,
    addressesList: isAvailableLength ? addressesList : [],
    isLoading,
  });
};

export default memo(SearchAddress);
